import { formatDate } from 'util/formatDate';
import { formatTime } from 'util/formatTime';
import { Image, Link, TruncateText } from 'components/lib';
import { Box, Typography, keyframes } from '@mui/material';
import React, { ReactElement } from 'react';
import log from 'loglevel';
import { FavoriteEventButton } from 'components/app/FavoriteEventButton';
import { Event } from 'services/api';
import { colorWhite, colorSkeletonBackground } from 'customColors';

function checkIsHappeningNow(date: Date, startTime?: string, endTime?: string): boolean {
  if (!startTime || !endTime) return false;

  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);

  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();

  const start = new Date(y, m, d, startH, startM, 0, 0);
  const end = new Date(y, m, d, endH, endM, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  const now = new Date();
  return now >= start && now <= end;
}

export interface EventBriefProps {
  id: Event['id'];
  title: string;
  link: string;
  date: Date;
  favorite?: boolean;
  hideFavorite?: boolean;
  startTime?: string;
  endTime?: string;
  bannerSrc: string;
  isHappeningNow?: boolean;
  school: {
    iconUrl: string;
    logoUrl: string;
    link: string;
    name: string;
  };
  organizations: {
    logoUrl: string;
    link: string;
    name: string;
    id?: string;
  }[];
  onOrganizationClick?: (organizationId: string, organizationName: string, organizationLogoUrl: string, schoolSlug?: string) => void;
  style?: object | [];
  disableLinks?: boolean;
  maxWidth?: object;
  minWidth?: object;
  hideBorder?: boolean;
  hideLogos?: boolean;
  ctaLabel?: string;
}

const pulse = keyframes`
  0%, 100% { opacity: 0.87; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.15); }
`;

const MAX_VISIBLE_ORGS = 3;

export const EventBrief = ({
  id,
  title,
  link,
  date,
  startTime,
  endTime,
  bannerSrc,
  school,
  isHappeningNow: isHappeningNowProp,
  organizations = [],
  onOrganizationClick,
  style = {},
  hideFavorite = false,
  disableLinks,
  hideBorder = false,
  hideLogos = false,
  ctaLabel = 'View Details',
}: EventBriefProps): ReactElement => {
  const isHappeningNow = isHappeningNowProp ?? checkIsHappeningNow(date, startTime, endTime);

  const totalOrgs = organizations.length;
  const showCountBadge = totalOrgs > MAX_VISIBLE_ORGS;
  const visibleOrgCount = showCountBadge ? MAX_VISIBLE_ORGS - 1 : totalOrgs;
  const hiddenOrgCount = totalOrgs - visibleOrgCount;

  if (endTime && !startTime)
    log.error(`endTime (${endTime}) with no startTime`);

  return (
    <Box
      sx={[
        ...(Array.isArray(style) ? style : [style]), { width: '100%'}
      ]}
    >
      <Link href={disableLinks ? undefined : link} style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%', height: '100%' }}>
        <Box
          sx={{
            backgroundColor: hideBorder ? 'transparent' : colorWhite,
            textAlign: 'left',
            border: hideBorder ? 'none' : `1px solid ${colorSkeletonBackground}`,
            lineHeight: 0,
            boxShadow: 0,
            overflow: 'hidden',
            transition: 'box-shadow 0.3s',
            cursor: 'pointer',
            height: '100%',
            '&:hover': {
              boxShadow: hideBorder ? 0 : '1px 1px 15px rgba(0,0,0,.2)',
            },
          }}
        >
        {!hideFavorite && (
          <Box
            position="relative"
            top={4}
            left={4}
            fontSize={35}
            height="0px"
            width="0px"
            zIndex={1}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <FavoriteEventButton eventId={id} />
          </Box>
        )}

        <Box
          sx={{
            width: '100%',
            aspectRatio: '3/4',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Image
            src={bannerSrc}
            alt={title}
            width="100%"
            height="100%"
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: colorWhite,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 197,
            px: 14,
            py: 16,
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: '24px',
                color: '#111111',
                height: 48,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <TruncateText lines={2}>{title}</TruncateText>
            </Typography>
          </Box>

          <Box sx={{ width: '100%', height: 27, display: 'flex', alignItems: 'flex-end' }}>
            {isHappeningNow ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4.761px',
                  backgroundColor: '#FFF0F0',
                  border: '0.5px solid #FFCDD2',
                  borderRadius: '4px',
                  px: '8.5px',
                  py: '4.5px',
                }}
              >
                <Box
                  sx={{
                    width: 6.5,
                    height: 6.5,
                    borderRadius: '50%',
                    backgroundColor: '#E53935',
                    opacity: 0.87,
                    flexShrink: 0,
                    animation: `${pulse} 1.5s ease-in-out infinite`,
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: '18px',
                    color: '#C62828',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Happening Now
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '18px',
                  color: '#777777',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatDate(date, {
                  includeDay: true,
                  includeDate: true,
                  includeYear: true,
                })}
              </Typography>
            )}
          </Box>

          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: 12,
                lineHeight: '18px',
                color: '#777777',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {startTime && formatTime(startTime)}
              {startTime && endTime && ' – '}
              {endTime && formatTime(endTime)}
            </Typography>
          </Box>

          {/* Bottom row: badges + CTA */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {!hideLogos && (
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {organizations
                  .slice(0, visibleOrgCount)
                  .map(({ logoUrl, name, link: orgLink, id: orgId }, i) => (
                    <Box
                      key={i}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onOrganizationClick && orgId) {
                          const schoolSlug = school.link?.replace(/.*\/explore\//, '').replace(/^\//, '') || undefined;
                          onOrganizationClick(orgId, name, logoUrl, schoolSlug);
                        } else if (!disableLinks && orgLink) {
                          window.location.href = orgLink;
                        }
                      }}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Image
                        src={logoUrl}
                        alt={name}
                        width={40}
                        height={40}
                        style={{
                          objectFit: 'contain',
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  ))}

                {showCountBadge && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '6px',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 400,
                        fontSize: 12,
                        color: '#383838',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +{hiddenOrgCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Box
              sx={{
                backgroundColor: '#FFE733',
                height: 48,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 14,
                py: 7,
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: 12,
                  color: '#383838',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  lineHeight: 'normal',
                }}
              >
                {ctaLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
        </Box>
      </Link>
    </Box>
  );
};
